import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, IconButton, Paper, Portal, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import { useFeatureFlags } from '../admin/featureFlags/FeatureGate';
import { GUIDES } from './tours';
import type { Guide } from './types';
import {
  guideFor,
  normalizePath,
  proposalFor,
  stepDestination,
  stepIndexForScreen,
  stepMatches,
  targetSelector,
  type StepDestination,
} from './guideRules';
import {
  clearHighlight,
  closeGuide,
  closeViewer,
  closeWhatsNew,
  dismissProposal,
  goToStep,
  markGuideSeen,
  openGuide,
  openViewer,
  openWhatsNew,
  requestHighlight,
  setCollapsed,
  syncGuideUser,
  useGuideState,
  type GuideState,
} from './guideStore';
import { clampPosition, readPosition, writePosition, type PanelPosition } from './guideStorage';
import { slideFor, useSlidesManifest, type SlidesManifest } from './slides';
import { findVisible, GuideHighlight, HIGHLIGHT_COLOR, type HighlightStatus } from './GuideHighlight';
import { SlideViewer } from './SlideViewer';
import { WhatsNewDialog } from './WhatsNewDialog';

/**
 * Przewodnik po zmianach — nakładka w aplikacji (decyzja użytkownika
 * 21.10.2026, na prośbę klienta; przełącznik `guideOverlay`, S-21.10-28).
 *
 * Mały, przesuwalny panel prowadzi przez funkcję „do decyzji” krok po kroku:
 * „Co to robi”, krok z opisem i podpowiedzią działania, miniatura slajdu
 * nagranego przed wdrożeniem (klik — duży podgląd). Odnosi się do bieżącego
 * ekranu: wybiera krok tego ekranu, „Pokaż na ekranie” podświetla element,
 * a gdy kroku nie ma na tym ekranie — mówi, dokąd przejść, i prowadzi tam.
 * „Dalej” / „Wstecz” przechodzą między slajdami i ekranami.
 *
 * Na ekranie z nową funkcją, której przewodnika użytkownik nie oglądał,
 * panel tylko **proponuje** („Nowe: … — pokaż, jak działa”) — nic nie
 * wymusza. Przewodniki dotyczą funkcji włączonych dla konta, czyli tego,
 * co widać w wersji „nowa (do akceptacji)”. Nakładka nie łapie kliknięć
 * poza swoim panelem; podświetlenie jest przezroczyste dla myszy.
 *
 * Zrzuty nagrywarki (`e2e/guides/record.mjs`) chowają nakładkę po atrybucie
 * `data-guide-overlay` — slajd pokazuje sam ekran.
 */

export const PANEL_WIDTH = 360;
/**
 * Warstwy (integracja fali C, 22.10.2026). Otwarty panel i jego pigułka stoją
 * nad oknami dialogowymi (MUI: 1300) — kroki prowadzą także po oknach.
 * **Propozycja** „Nowe: …” to tylko podpowiedź, więc stoi pod oknami: przy
 * otwartym oknie chowa się pod jego tłem — dotąd zasłaniała przyciski okna
 * („Potwierdź zapis” w eksporcie do TMS, „Kartoteka” na karcie pojazdu),
 * co wyszło w lokalnym e2e. Nad paskiem górnym i menu (1100, 1200) zostaje.
 */
export const PANEL_Z_INDEX = 1350;
export const PROPOSAL_Z_INDEX = 1250;
/** Odstęp od prawej krawędzi — pasek przewijania strony zostaje odkryty. */
const EDGE = 24;
const MOVE_STEP = 24;

const viewport = () => ({ width: window.innerWidth, height: window.innerHeight });

/** Czy element kroku jest teraz na ekranie. */
const onScreen = (target: string) => findVisible(targetSelector(target)) !== null;

/** Krok, od którego przewodnik startuje na tym ekranie (krok z widocznym elementem). */
const startStep = (guide: Guide, path: string) => Math.max(0, stepIndexForScreen(guide, path, onScreen));

/** Położenie pływającego elementu: zapamiętane (lewy górny róg) albo prawy dolny róg. */
const anchorSx = (pos: PanelPosition | null) =>
  pos ? { left: pos.x, top: pos.y } : { right: EDGE, bottom: EDGE };

export const GuideOverlay = ({ guides = GUIDES }: { guides?: readonly Guide[] }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = normalizePath(location.pathname);
  const effective = useFeatureFlags();
  const s = useGuideState();
  const { manifest, loading: slidesLoading } = useSlidesManifest();
  const panelRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<HighlightStatus | null>(null);
  const [pos, setPos] = useState<PanelPosition | null>(() => readPosition());

  // Inne konto w tej karcie — pamięć przewodnika od nowa.
  useEffect(() => {
    syncGuideUser();
  });

  const isEnabled = useCallback((key: string) => effective?.flags[key] === true, [effective]);
  const guide = guideFor(guides, s.openKey);
  const step = guide?.steps[s.step] ?? null;

  // Operator przeszedł na inny ekran sam: krok idzie za ekranem, jeśli
  // przewodnik ma krok na tym ekranie (bieżący krok tego ekranu zostaje).
  const lastPath = useRef(path);
  useEffect(() => {
    if (lastPath.current === path) return;
    lastPath.current = path;
    if (!guide || (step && stepMatches(step, path))) return;
    const i = stepIndexForScreen(guide, path, onScreen);
    if (i >= 0) goToStep(i);
  }, [path, guide, step]);

  // Okno zmienia rozmiar — panel zostaje w granicach.
  useEffect(() => {
    const onResize = () =>
      setPos((p) => {
        const r = panelRef.current?.getBoundingClientRect();
        return p && r ? clampPosition(p, { width: r.width, height: r.height }, viewport()) : p;
      });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const destination: StepDestination | null = guide ? stepDestination(guide, s.step, path) : null;

  /** Krok `index` — przejście na jego ekran (gdy znany adres) i podświetlenie. */
  const goTo = (index: number) => {
    if (!guide) return;
    const dest = stepDestination(guide, index, path);
    goToStep(index);
    if (dest.kind === 'route') navigate(dest.path);
  };

  /** „Pokaż na ekranie” / „Przejdź” / „Otwórz listę”. */
  const show = () => {
    if (!destination) return;
    if (destination.kind === 'record') {
      navigate(destination.listPath);
      return;
    }
    if (destination.kind === 'route') navigate(destination.path);
    requestHighlight();
  };

  const highlightOn = !!guide && !!step && !!s.highlight && s.highlight.flagKey === guide.flagKey && s.highlight.step === s.step;

  const viewerGuide = s.viewer ? guideFor(guides, s.viewer.flagKey) : null;
  const whatsNew = guides.filter((g) => isEnabled(g.flagKey));
  const proposal = !guide && s.enabled ? proposalFor(guides, path, isEnabled, s.seen, s.dismissed) : null;

  // Portal do `body`: układ panelu tworzy własny kontekst warstw, w którym panel
  // przegrywał z paskiem górnym (sprawdzenie w przeglądarce 21.10.2026).
  return (
    <Portal>
    <Box data-guide-overlay="" data-testid="guide-overlay">
      {s.enabled && guide && step && !s.collapsed ? (
        <GuidePanel
          panelRef={panelRef}
          pos={pos}
          setPos={setPos}
          guide={guide}
          state={s}
          destination={destination}
          featureOn={isEnabled(guide.flagKey)}
          versionSwitch={!!effective?.versionSwitchAvailable}
          status={highlightOn ? status : null}
          highlightOn={highlightOn}
          manifest={manifest}
          slidesLoading={slidesLoading}
          onShow={show}
          onPrev={() => goTo(s.step - 1)}
          onNext={() => goTo(s.step + 1)}
        />
      ) : null}
      {s.enabled && guide && step && s.collapsed ? (
        <Paper elevation={6} sx={{ position: 'fixed', zIndex: PANEL_Z_INDEX, ...anchorSx(pos) }}>
          <Button
            size="small"
            startIcon={<SchoolOutlinedIcon />}
            onClick={() => setCollapsed(false)}
            data-testid="guide-pill"
            aria-label={`Rozwiń przewodnik: ${guide.title}, krok ${s.step + 1} z ${guide.steps.length}`}
            sx={{ textTransform: 'none', px: 1.5 }}
          >
            Przewodnik: {guide.title} · {s.step + 1}/{guide.steps.length}
          </Button>
        </Paper>
      ) : null}
      {proposal ? (
        <Paper
          elevation={6}
          role="region"
          aria-label="Propozycja przewodnika"
          data-testid="guide-proposal"
          sx={{ position: 'fixed', zIndex: PROPOSAL_Z_INDEX, width: PANEL_WIDTH, p: 1.5, ...anchorSx(pos) }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <TipsAndUpdatesOutlinedIcon sx={{ color: HIGHLIGHT_COLOR, mt: 0.25 }} fontSize="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Nowe: {proposal.guide.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                Pokażemy krok po kroku, jak działa i co kliknąć.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => openGuide(proposal.guide.flagKey, startStep(proposal.guide, path))}
                  data-testid="guide-proposal-open"
                >
                  Pokaż, jak działa
                </Button>
                <Button size="small" onClick={() => dismissProposal(proposal.guide.flagKey)} data-testid="guide-proposal-dismiss">
                  Nie teraz
                </Button>
                <Button size="small" onClick={() => markGuideSeen(proposal.guide.flagKey)} data-testid="guide-proposal-never">
                  Nie pokazuj
                </Button>
              </Stack>
              {proposal.others > 0 ? (
                <Button size="small" onClick={openWhatsNew} sx={{ mt: 0.5, px: 0 }} data-testid="guide-proposal-more">
                  Nowych funkcji na tym ekranie: jeszcze {proposal.others} — „Co nowego”
                </Button>
              ) : null}
            </Box>
          </Stack>
        </Paper>
      ) : null}
      {s.enabled && highlightOn && step ? (
        <GuideHighlight
          selector={targetSelector(step.target)}
          token={s.highlight?.token ?? 0}
          panelRef={s.collapsed ? undefined : panelRef}
          onStatus={setStatus}
        />
      ) : null}
      {s.whatsNewOpen ? (
        <WhatsNewDialog
          guides={whatsNew}
          seen={s.seen}
          onClose={closeWhatsNew}
          onOpen={(key) => {
            const g = guideFor(guides, key);
            openGuide(key, g ? startStep(g, path) : 0);
          }}
        />
      ) : null}
      {s.viewer && viewerGuide ? (
        <SlideViewer
          guide={viewerGuide}
          step={s.viewer.step}
          manifest={manifest}
          onStep={(i) => openViewer(viewerGuide.flagKey, i)}
          onClose={closeViewer}
          onShowInApp={(i) => {
            closeViewer();
            if (s.openKey !== viewerGuide.flagKey) openGuide(viewerGuide.flagKey, i);
            const dest = stepDestination(viewerGuide, i, path);
            goToStep(i);
            if (dest.kind === 'route') navigate(dest.path);
            if (dest.kind === 'record') navigate(dest.listPath);
          }}
        />
      ) : null}
    </Box>
    </Portal>
  );
};

/** Opis, dokąd przejść, gdy elementu nie ma na ekranie albo krok jest gdzie indziej. */
export function whereText(destination: StepDestination | null, status: HighlightStatus | null, guide: Guide): string | null {
  if (!destination) return null;
  if (destination.kind === 'record') return `Ten krok dotyczy konkretnego rekordu: ${destination.description}. Otwórz listę i wybierz taki rekord.`;
  if (destination.kind === 'route') return 'Ten krok jest na innym ekranie — przejdziemy tam i pokażemy element.';
  if (status === 'missing') {
    return `Nie widzę tego elementu na ekranie. Bywa niżej na stronie, w zwiniętej sekcji, w oknie, które otwiera poprzedni krok, albo tylko dla roli: ${guide.audience.join(', ')}.`;
  }
  return null;
}

const GuidePanel = ({
  panelRef,
  pos,
  setPos,
  guide,
  state,
  destination,
  featureOn,
  versionSwitch,
  status,
  highlightOn,
  manifest,
  slidesLoading,
  onShow,
  onPrev,
  onNext,
}: {
  panelRef: RefObject<HTMLDivElement>;
  pos: PanelPosition | null;
  setPos: (p: PanelPosition | null) => void;
  guide: Guide;
  state: GuideState;
  destination: StepDestination | null;
  featureOn: boolean;
  versionSwitch: boolean;
  status: HighlightStatus | null;
  highlightOn: boolean;
  manifest: SlidesManifest | null;
  slidesLoading: boolean;
  onShow: () => void;
  onPrev: () => void;
  onNext: () => void;
}) => {
  const step = guide.steps[state.step];
  const total = guide.steps.length;
  const last = state.step >= total - 1;
  const slide = slideFor(manifest, guide.flagKey, state.step);
  const [more, setMore] = useState(false);
  const titleRef = useRef<HTMLElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const posRef = useRef(pos);
  posRef.current = pos;

  // Otwarcie przewodnika — fokus na tytule panelu (klawiatura trafia do panelu).
  useEffect(() => {
    titleRef.current?.focus();
  }, [guide.flagKey]);

  const place = (p: PanelPosition) => {
    const r = panelRef.current?.getBoundingClientRect();
    const next = clampPosition(p, { width: r?.width ?? PANEL_WIDTH, height: r?.height ?? 200 }, viewport());
    posRef.current = next;
    setPos(next);
    return next;
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const r = panelRef.current?.getBoundingClientRect();
    if (!r) return;
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Przeglądarka bez przechwytywania wskaźnika — przeciąganie i tak działa w obrębie paska.
    }
    e.preventDefault();
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    place({ x: e.clientX - d.dx, y: e.clientY - d.dy });
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    drag.current = null;
    writePosition(posRef.current);
  };
  /** Strzałki przesuwają panel, Home wraca do prawego dolnego rogu. */
  const onHandleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const r = panelRef.current?.getBoundingClientRect();
    const from = posRef.current ?? (r ? { x: r.left, y: r.top } : null);
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-MOVE_STEP, 0],
      ArrowRight: [MOVE_STEP, 0],
      ArrowUp: [0, -MOVE_STEP],
      ArrowDown: [0, MOVE_STEP],
    };
    if (e.key === 'Home') {
      e.preventDefault();
      posRef.current = null;
      setPos(null);
      writePosition(null);
      return;
    }
    const d = delta[e.key];
    if (!d || !from) return;
    e.preventDefault();
    writePosition(place({ x: from.x + d[0], y: from.y + d[1] }));
  };

  const where = whereText(destination, status, guide);
  const showLabel =
    destination?.kind === 'record' ? 'Otwórz listę' : destination?.kind === 'route' ? 'Przejdź i pokaż' : 'Pokaż na ekranie';

  return (
    <Paper
      ref={panelRef}
      elevation={8}
      role="dialog"
      aria-modal="false"
      aria-labelledby="guide-panel-title"
      data-testid="guide-panel"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          setCollapsed(true);
        }
      }}
      sx={{
        position: 'fixed',
        zIndex: PANEL_Z_INDEX,
        width: PANEL_WIDTH,
        maxWidth: `calc(100vw - ${2 * EDGE}px)`,
        maxHeight: 'min(78vh, 640px)',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `4px solid ${HIGHLIGHT_COLOR}`,
        ...anchorSx(pos),
      }}
    >
      <Box
        data-testid="guide-panel-handle"
        tabIndex={0}
        aria-label="Pasek panelu przewodnika — przeciągnij albo przesuń strzałkami; Home wraca do rogu ekranu"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onHandleKey}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          cursor: 'move',
          userSelect: 'none',
          touchAction: 'none',
          bgcolor: 'action.hover',
        }}
      >
        <SchoolOutlinedIcon fontSize="small" sx={{ color: HIGHLIGHT_COLOR }} />
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>
          Przewodnik po zmianach
        </Typography>
        {guide.presentationItems.map((i) => (
          <Chip key={i} size="small" label={i} variant="outlined" sx={{ height: 20 }} />
        ))}
        <Tooltip title="Zwiń (Esc)">
          <IconButton size="small" aria-label="Zwiń przewodnik" onClick={() => setCollapsed(true)} data-testid="guide-collapse">
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zamknij przewodnik">
          <IconButton size="small" aria-label="Zamknij przewodnik" onClick={closeGuide} data-testid="guide-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ px: 1.5, py: 1, overflowY: 'auto' }}>
        <Typography
          id="guide-panel-title"
          ref={titleRef}
          tabIndex={-1}
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.3, outline: 'none' }}
        >
          {guide.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="guide-what-it-does"
          sx={
            more
              ? { mt: 0.5 }
              : { mt: 0.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
          }
        >
          <strong>Co to robi: </strong>
          {guide.whatItDoes}
        </Typography>
        <Button size="small" onClick={() => setMore((m) => !m)} sx={{ px: 0, minWidth: 0 }}>
          {more ? 'mniej' : 'więcej'}
        </Button>
        <Typography variant="caption" color="text.secondary" component="div">
          Dla kogo: {guide.audience.join(', ')}
        </Typography>

        {!featureOn ? (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="guide-feature-off">
            {versionSwitch
              ? 'Ta funkcja jest wyłączona w wersji aplikacji, z której korzystasz — w menu użytkownika wybierz „Wersja: nowa (do akceptacji)”.'
              : 'Ta funkcja nie jest włączona na tym środowisku — przewodnik opisuje ją, ale na ekranie jej nie zobaczysz.'}
          </Alert>
        ) : null}

        <Box sx={{ mt: 1.5 }} data-testid="guide-step" data-step={state.step + 1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Krok {state.step + 1} z {total}: {step.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {step.description}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }} data-testid="guide-action">
            Co zrobić: {step.action}
          </Typography>
        </Box>

        <Box sx={{ mt: 1 }} data-testid="guide-slide">
          {slide ? (
            <>
              <Box
                component="button"
                type="button"
                onClick={() => openViewer(guide.flagKey, state.step)}
                aria-label={`Powiększ slajd ${state.step + 1}: ${step.title}`}
                data-testid="guide-slide-thumb"
                sx={{ p: 0, border: 1, borderColor: 'divider', borderRadius: 1, cursor: 'zoom-in', display: 'block', width: '100%', bgcolor: 'transparent' }}
              >
                <Box component="img" src={slide.url} alt="" sx={{ display: 'block', width: '100%', borderRadius: 1 }} />
              </Box>
              <Button size="small" startIcon={<ZoomInIcon />} onClick={() => openViewer(guide.flagKey, state.step)} data-testid="guide-slide-zoom">
                Powiększ
              </Button>
            </>
          ) : slidesLoading ? null : (
            <Typography variant="caption" color="text.secondary" data-testid="guide-no-slides">
              Slajdów dla tego kroku jeszcze nie nagrano — opis i podświetlenie na ekranie działają bez nich.
            </Typography>
          )}
        </Box>

        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {where ? (
            <Typography variant="body2" color={status === 'missing' ? 'warning.main' : 'text.secondary'} data-testid="guide-where">
              {where}
            </Typography>
          ) : null}
          {highlightOn && status === 'found' && destination?.kind === 'here' ? (
            <Typography variant="caption" color="text.secondary" data-testid="guide-found">
              Element jest podświetlony na ekranie.
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={onShow} data-testid="guide-show-on-screen">
              {showLabel}
            </Button>
            {highlightOn ? (
              <Button size="small" onClick={clearHighlight} data-testid="guide-hide-highlight">
                Ukryj podświetlenie
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
        <Button size="small" onClick={onPrev} disabled={state.step === 0} data-testid="guide-prev">
          Wstecz
        </Button>
        <Typography variant="caption" color="text.secondary">
          {state.step + 1} / {total}
        </Typography>
        {last ? (
          <Button size="small" variant="contained" onClick={closeGuide} data-testid="guide-finish">
            Zakończ
          </Button>
        ) : (
          <Button size="small" variant="contained" onClick={onNext} data-testid="guide-next">
            Dalej
          </Button>
        )}
      </Box>
    </Paper>
  );
};
